import classNames from 'classnames';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import useFetchMoreActivities, {
  nextBeforeAt,
} from '../../activities/useFetchMoreActivities';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import { useQuill } from '../../editor/QuillContext';
import isInlineTextInsertion from '../../editor/utils/isInlineTextInsertion';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostActivityAction } from '../../graphql/generated/graphql';
import graphStatsIcon from '../../icons/non-prefilled/16x16/graphStats.svg';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';
import ControlBarActivity, {
  CONTROL_BAR_ACTIVITY_MIN_HEIGHT,
} from './ControlBarActivity';
import * as styles from './ControlBarActivityButton.module.scss';
import ControlBarButton from './ControlBarButton';
import ControlBarPopOver from './ControlBarPopOver';

const POST_FRAGMENT = gql(`
  fragment ControlBarActivityButton_post on Post {
    id
    insertedAt
    publishedAt
    userViewedAt
    threads {
      id
    }
    ...usePostPolicyCheck_post
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields -- select source.id because it's required as a key in typePolicies
const POST_ACTIVITIES_FRAGMENT = gql(`
  fragment ControlBarActivityButton_activities on PostActivity {
    actedAt
    action
    actors {
      id
    }
    source {
      id
    }
  }
`);

const POPOVER_POST_QUERY = gql(/** @complexity 11 */ `
  query ControlBarActivityPost($postId: ID!) {
    post(id: $postId)  {
      id
      ...ControlBarActivity_post
    }
  }
`);

const POPOVER_ACTIVITIES_QUERY = gql(/** @complexity 20 */ `
  query ControlBarActivityList($postId: ID!, $beforeAt: Datetime!) {
    postActivities(postId: $postId, beforeAt: $beforeAt) {
      ...ControlBarActivity_activities
    }
  }
`);

interface ControlBarActivityButtonProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  post: DocumentType<typeof POST_FRAGMENT>;
  activities: DocumentType<typeof POST_ACTIVITIES_FRAGMENT>[];
}

const ControlBarActivityButton = ({
  open,
  setOpen,
  post,
  activities,
}: ControlBarActivityButtonProps) => {
  const [isPrefetchingPopOverData, setIsPrefetchingPopOverData] =
    useState(false);
  const [isPreviousOpen, setIsPreviousOpen] = useState(open);
  if (isPreviousOpen !== open) {
    setIsPreviousOpen(open);
    setIsPrefetchingPopOverData(false);
  }

  const skipPopoverQuery = !open && !isPrefetchingPopOverData;

  // useQuery doesn't update `loading` in the result after
  // toggling `skip` unless we change the query variables
  const postId = skipPopoverQuery ? '' : post.id;

  const popoverActivitiesQuery = useQuery(POPOVER_ACTIVITIES_QUERY, {
    variables: {
      postId,
      beforeAt: nextBeforeAt(),
    },
    skip: skipPopoverQuery,
  });

  const popoverPostQuery = useQuery(POPOVER_POST_QUERY, {
    variables: { postId },
    skip: skipPopoverQuery,
  });

  const fetchMoreActivities = useFetchMoreActivities(
    popoverActivitiesQuery,
    'postActivities',
  );

  const postRenderTime = useMemo(
    () => ({ postId: post.id, time: moment() }), // Reset when active post changes
    [post.id],
  ).time;

  const unresolvedCommentThreadIds = useUnresolvedCommentThreadIds(post);

  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();
  const canEdit = postPolicyCheck(PostPolicyAction.EditPost, post, currentUser);

  const [lastUserViewBeforeOpen, setLastUserViewBeforeOpen] = useState<
    number | string
  >(post.userViewedAt ?? 0);

  if (
    lastUserViewBeforeOpen !== post.userViewedAt &&
    postRenderTime.isAfter(post.userViewedAt)
  ) {
    setLastUserViewBeforeOpen(post.userViewedAt);
  }

  const lastUserActivityBeforeOpen =
    activities.find(
      (activity) =>
        activity.actors.some((actor) => actor.id === currentUser.id) &&
        postRenderTime.isAfter(activity.actedAt),
    )?.actedAt ?? 0;

  const lastViewedAt = Math.max(
    new Date(lastUserViewBeforeOpen).getTime(),
    new Date(lastUserActivityBeforeOpen).getTime(),
  );

  const hasNewActivities =
    lastViewedAt !== 0 &&
    activities.some((activity) => {
      const isEmptyEdit =
        activity.actors.length === 0 &&
        activity.action === PostActivityAction.Edit;

      return (
        moment(activity.actedAt).isAfter(lastViewedAt) &&
        !isEmptyEdit &&
        !activity.actors.some((actor) => actor.id === currentUser.id)
      );
    });

  const isNewDraft =
    !post.publishedAt &&
    !activities.some((item) => item.action === PostActivityAction.Publish) &&
    moment(post.insertedAt).add(1, 'hour').isAfter(postRenderTime);

  return (
    !isNewDraft && (
      <ControlBarPopOver
        content={
          <ControlBarActivity
            post={
              popoverPostQuery.error ? undefined : popoverPostQuery.data?.post
            }
            activities={
              popoverActivitiesQuery.error
                ? undefined
                : popoverActivitiesQuery.data?.postActivities
            }
            unresolvedCommentThreadIds={unresolvedCommentThreadIds}
            fetchMoreActivities={fetchMoreActivities}
            closePopover={() => setOpen(false)}
            lastViewedAt={lastViewedAt}
            allowUnresolvedThreadsSection={canEdit}
          />
        }
        open={open}
        onOpenChange={setOpen}
        placement="bottom-start"
        preventScreenOverflow={{
          minHeight: CONTROL_BAR_ACTIVITY_MIN_HEIGHT,
        }}
      >
        <ControlBarButton
          active={open}
          aria-label="Post activities"
          onPointerDown={() => {
            if (!open) {
              setIsPrefetchingPopOverData(true);
            }
          }}
          icon={
            <SVGIcon
              className={classNames({
                [styles.hasNewActivities]: hasNewActivities,
                [styles.hasUnresolvedCommentThreads]:
                  canEdit && unresolvedCommentThreadIds.length > 0,
              })}
            >
              {graphStatsIcon}
            </SVGIcon>
          }
        />
      </ControlBarPopOver>
    )
  );
};

const useUnresolvedCommentThreadIds = (
  post: DocumentType<typeof POST_FRAGMENT>,
) => {
  const { quill } = useQuill();
  const [postThreadsIds, setPostThreadsIds] = useState(new Set<string>());
  useEffect(() => {
    if (!quill) return undefined;

    const updatePostThreadsIds = () => {
      const result = collectThreadAndAnnotationIds(quill.getContents().ops);
      setPostThreadsIds((current) =>
        isEqual(current, result.allThreadIds) ? current : result.allThreadIds,
      );
    };

    updatePostThreadsIds();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      updatePostThreadsIds();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill]);

  return post.threads
    .map((thread) =>
      isCommentOrPublicCommentThread(thread) && postThreadsIds.has(thread.id)
        ? thread.id
        : null,
    )
    .filter((item) => item != null);
};

export default ControlBarActivityButton;
